import { navigateTo } from "nuxt/app";
import type { LocationQueryRaw } from "vue-router";
import type { MainViewMode } from "~/types/entity/cWebCatalog";
import { getNavigationRoute } from "~/utils/urlHelper";
import {
  isAllowedExternalNavigationUrl,
  isInternalAppPath,
  sanitizePathForTelemetry,
} from "~/utils/navigationGuard";
import { trackClientException } from "~/composables/trackClientException";

export type NavigateToRouteOptions = {
  query?: LocationQueryRaw
  hash?: string
  replace?: boolean
  redirectCode?: number
  /**
   * 同一オリジンでも Nuxt external 遷移 (フルリロード) にする。
   * ADR-007 課金導線で △ページ状態破棄に使う。真の外部 URL は navigateToExternal。
   */
  forceExternal?: boolean
};

const isAbsoluteUrl = (path: string): boolean => /^https?:\/\//i.test(path);

const trackNavigationError = (message: string, properties?: Record<string, string>): void => {
  trackClientException(new Error(message), properties);
};

const hasMatchedRoute = (to: string | { path: string, query?: LocationQueryRaw, hash?: string }): boolean => {
  const router = useRouter();
  return router.resolve(to).matched.length > 0;
};

/**
 * MainViewMode 経由のアプリ内 SPA 遷移。
 * ルート未登録・router.resolve 不一致時は遷移せず telemetry 報告する。
 * 絶対 URL の Mode (外部リンク) は navigateToExternal 経由で遷移する。
 */
export const navigateToRoute = async (
  mode: MainViewMode,
  options?: NavigateToRouteOptions
): Promise<boolean> => {
  const path = getNavigationRoute(mode);
  if (!path) {
    trackNavigationError(`Navigation route not mapped: mode=${mode}`, { mode });
    return false;
  }
  if (isAbsoluteUrl(path)) {
    return navigateToExternal(path, { replace: options?.replace });
  }
  const to = (options?.query !== undefined || options?.hash !== undefined)
    ? { path, query: options?.query, hash: options?.hash }
    : path;
  if (!hasMatchedRoute(to)) {
    const safePath = sanitizePathForTelemetry(path);
    trackNavigationError(`Navigation target not resolved: mode=${mode} path=${safePath}`, { mode, path: safePath });
    return false;
  }
  await navigateTo(to, {
    external: options?.forceExternal === true,
    replace: options?.replace,
    redirectCode: options?.redirectCode,
  });
  return true;
};

/**
 * 他オリジン等の絶対 URL 専用遷移。許可プロトコル・ホストのみ。
 * 同一オリジンの `/pay` などは navigateToRoute(..., { forceExternal: true }) を使う。
 */
export const navigateToExternal = async (
  url: string,
  options?: { replace?: boolean }
): Promise<boolean> => {
  if (!url) {
    trackNavigationError("navigateToExternal: empty url");
    return false;
  }
  if (!isAllowedExternalNavigationUrl(url)) {
    const safePath = sanitizePathForTelemetry(url);
    trackNavigationError(`navigateToExternal: disallowed url path=${safePath}`, { path: safePath });
    return false;
  }
  await navigateTo(url, { ...options, external: true });
  return true;
};

/**
 * sanitize 済み等の内部パス文字列向け。深リンク復帰・課金後復帰専用。
 * 絶対 URL / scheme-relative は拒否し、外部遷移は navigateToExternal に限定する。
 */
export const navigateToSafePath = async (
  path: string,
  options?: { fallbackMode?: MainViewMode, replace?: boolean }
): Promise<boolean> => {
  if (!path) {
    trackNavigationError("navigateToSafePath: empty path");
    if (options?.fallbackMode) {
      return navigateToRoute(options.fallbackMode, { replace: options.replace });
    }
    return false;
  }
  if (!isInternalAppPath(path)) {
    const safePath = sanitizePathForTelemetry(path);
    trackNavigationError(`navigateToSafePath: non-internal path=${safePath}`, { path: safePath });
    if (options?.fallbackMode) {
      return navigateToRoute(options.fallbackMode, { replace: options.replace });
    }
    return false;
  }
  if (!hasMatchedRoute(path)) {
    const safePath = sanitizePathForTelemetry(path);
    trackNavigationError(`Navigation target not resolved: path=${safePath}`, { path: safePath });
    if (options?.fallbackMode) {
      return navigateToRoute(options.fallbackMode, { replace: options.replace });
    }
    return false;
  }
  await navigateTo(path, { replace: options?.replace });
  return true;
};

/**
 * ログイン後の戻り先 (initialRoute) へ遷移する。
 * clear / setStateFromUrl は呼び出し側の責務。遷移直前の resolve 検証のみ行う。
 * @param routeFullPath 既に取得済みの path。未指定時は getInitialRoute() を参照する。
 */
export const navigateToSavedInitialRoute = async (options?: {
  routeFullPath?: string
  fallbackMode?: MainViewMode
}): Promise<boolean> => {
  const fallbackMode = options?.fallbackMode ?? "TOP_PAGE";
  const initialRoute = options?.routeFullPath ?? useInitialRouteState().getInitialRoute();
  if (!initialRoute || initialRoute.startsWith("/auth/")) {
    return navigateToRoute(fallbackMode);
  }
  return navigateToSafePath(initialRoute, { fallbackMode });
};

/**
 * 課金開始ページ (/pay) へ遷移する。
 * 既定でフルリロード (forceExternal)。ADR-007 課金導線向け。
 * SPA 内の軽い遷移のみ forceExternal: false を指定する。
 */
export const navigateToPaymentStart = async (options?: {
  from?: string
  forceExternal?: boolean
}): Promise<boolean> => {
  const forceExternal = options?.forceExternal ?? true;
  const from = options?.from;
  return navigateToRoute("PAYMENT_START", {
    query: from ? { from } : undefined,
    forceExternal,
  });
};
